import { create } from 'zustand';
import type { AppLocale, ContractTemplateCategory, ContractTemplateStatus } from '@shared/types';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';
import type { ContractTemplateSort } from './contract-templates-selectors';

export type ContractTemplateEditorTab = 'general' | 'body' | 'placeholders' | 'outputRules' | 'versions';

interface ContractTemplateSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
}

function createInitialSelectionSession(): ContractTemplateSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0
  };
}

interface ContractTemplatesViewStore {
  statusFilter: 'all' | ContractTemplateStatus;
  categoryFilter: 'all' | ContractTemplateCategory;
  languageFilter: 'all' | AppLocale;
  sort: ContractTemplateSort;
  editorTab: ContractTemplateEditorTab;
  selectionSession: ContractTemplateSelectionSession;
  setStatusFilter: (value: 'all' | ContractTemplateStatus) => void;
  setCategoryFilter: (value: 'all' | ContractTemplateCategory) => void;
  setLanguageFilter: (value: 'all' | AppLocale) => void;
  setSort: (value: ContractTemplateSort) => void;
  setEditorTab: (value: ContractTemplateEditorTab) => void;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
}

export const useContractTemplatesViewStore = create<ContractTemplatesViewStore>((set) => ({
  statusFilter: 'all',
  categoryFilter: 'all',
  languageFilter: 'all',
  sort: 'updated',
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCategoryFilter: (categoryFilter) => set({ categoryFilter }),
  setLanguageFilter: (languageFilter) => set({ languageFilter }),
  setSort: (sort) => set({ sort }),
  setEditorTab: (editorTab) => set({ editorTab }),
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    }))
}));
